import { post } from '~shared/utils/http';
import { buildSpectateUrl } from '~spa/Utils/Spectate';
import {
    seoUrlSport,
    seoUrlSportAntepost,
    seoUrlTournament,
    seoUrlInplaySport,
    seoUrlInplay,
    seoUrlEvent,
    seoUrlInplayEvent,
    seoUrlCoupon,
    seoUrlCouponSport,
    seoUrlCouponTournament,
    seoUrlCompetitionSport,
    isSportPageContext,
    seoUrlRacing,
    seoUrlInplayScheduled,
    getSportsPageBreadcrumb,
    seoUrlTournamentOutrights,
    seoUrlTournamentOutright,
} from '~spa/Utils/SeoUrls';
import i18n from '~shared/utils/i18n';
import { getLanguageCode } from '../../../shared/utils/user';
import format from 'date-fns/format';
import { inplayHomepageUrl, inplayScheduledUrl } from '../../Utils/LocalizedUrls';
import { TOP_MENU_SCHEDULE_TEXT } from '../../../scenes/inplay/components/constants';
import { SEO_URL_SUFFIX_TOURNAMENT_OUTRIGHT } from '~spa/Utils/SeoUrls';

export const BREADCRUMBS_L10N_ENDPOINT = '/translation/breadcrumbs';

export const EVENT_TIME_FORMAT = 'HH:mm';

const STATIC_PAGE_BREADCRUMBS_TRANSLATION = {
    'betfeed': 'breadcrumbs.betfeed',
    'bets': 'breadcrumbs.bethistory',
    'betbuilder': 'breadcrumbs.brightbundles',
    'my-rewards': 'breadcrumbs.my_rewards',
    'madeforyou': 'breadcrumbs.madeforyou',
    'search': 'breadcrumbs.search',
};


export const getStaticPageBreadcrumbsTranslationKey = (pageIdentifier) => {
    return STATIC_PAGE_BREADCRUMBS_TRANSLATION[pageIdentifier] || null;
};

/**
 * Creates the data structure of a breadcrumbs item
 *
 * @param {String} label The label
 * @param {String} url The url
 * @param {Boolean} lastVisible Whether this element is the last visible
 * @param {Boolean} invisible Whether to make this item invisible (ie: a meta tag for SEO purposes)
 *
 * @returns {Object} The data structure
 */
function createBreadcrumbsItem(label, url, lastVisible = false, invisible = false) {
    return {
        url,
        label,
        lastVisible,
        invisible,
    };
}

/**
 * Creates the data structure for the last visible breadcrumbs item.
 * This is a workaround to keep showing the last visible item with a different
 * style when there's an invisible item right after.
 * Since CSS doesn't support something like :not(.invisible):last-child or similar
 * combinations, we have to do this with javascript.
 *
 * @param {String} label The item label
 * @param {String} url The url
 * @returns {Object} The data structure
 */
function createLastVisibleBreadcrumbsItem(label, url) {
    return createBreadcrumbsItem(label, url, true);
}

/**
 * Creates the data structure for an invisible breadcrumbs item
 *
 * @param {String} label The item label
 * @param {String} url The url
 * @returns {Object} The data structure
 */
function createInvisibleBreadcrumbsItem(label, url) {
    return createBreadcrumbsItem(label, url, false, true);
}

/**
 * creates the SEO Navigation data
 *
 * @param {String|Number} contextId The context id
 * @param {Object} data The breadcrumbs data (context from context resolver endpoint)
 * @returns {Object} The SEO Navigation data structure
 */
export async function buildSeoNav(contextId, data) {
    const {
        sportName: engSportName,
        pageName = null,
        isInplay = null,
        isRacing = null,
        isCoupons = null,
        isVirtuals = null,
        isMotorSport = null,
        originalUrl,
        systemUrlPath,
        context,
    } = data;

    const isSportPage = isSportPageContext(context);

    if (systemUrlPath === '/') {
        return buildSeoNavHomepage();
    }

    if (contextId === null) {
        // Static pages
        return buildSeoNavStaticPageItem(pageName, originalUrl, isInplay);
    }

    // Not in English, need to fetch the translations
    let translatedData = {};

    const lang = getLanguageCode();
    if (lang !== 'eng' || data.eventId) {
        translatedData = await translateBreadcrumbsData(data);
    }

    const breadcrumbsData = {...data, ...translatedData};

    // Dynamic pages (ie: sport, inplay)
    if (isInplay) {
        return buildSeoNavInplayItems(pageName, originalUrl, breadcrumbsData);
    }

    // Racing pages (they require special handling showing the event as the second level)
    if (isRacing) {
       return buildSeoNavRacing(breadcrumbsData);
    }

    if (isMotorSport) {
        return buildSeoNavMotorSport(breadcrumbsData);
     }

    if (isCoupons) {
        return buildBreadcrumbsCouponsItems(pageName, originalUrl, breadcrumbsData);
    }

    if (isVirtuals) {
        return buildSeoNavVirtuals(breadcrumbsData);
    }

    if (isSportPage) {
        return buildBreadcrumbsForSportPage(breadcrumbsData, engSportName);
    }

    return buildSeoNavDynamic(breadcrumbsData);
}

/**
 * Retrieves the translation of breadcrumb data.
 *
 * @param {Object} data The data to translate
 * @returns {Object} The translated data
 */
export async function translateBreadcrumbsData(data) {
    const {
        sportId = null,
        sportSlug = null,
        sportName = null,
        couponId = null,
        couponSlug = null,
        couponName = null,
        categoryId = null,
        categorySlug = null,
        categoryName = null,
        tournamentId = null,
        tournamentSlug = null,
        tournamentName = null,
        eventId = null,
        eventSlug = null,
        eventName = null,
        isInplay = false,
        originalUrl = null,
    } = data;

    const translationRequestData = {};

    if (sportId !== null) {
        translationRequestData['sport'] = { id: sportId, label: sportName, slug: sportSlug };
    }

    if (couponId !== null) {
        translationRequestData['coupon'] = { id: couponId, label: couponName, slug: couponSlug };
    }

    if (categoryId !== null) {
        translationRequestData['category'] = { id: categoryId, label: categoryName, slug: categorySlug };
    }

    if (tournamentId !== null) {
        translationRequestData['tournament'] = { id: tournamentId, label: tournamentName, slug: tournamentSlug };
    }

    if (eventId !== null) {
        translationRequestData['event'] = {
            id: eventId,
            label: eventName,
            slug: eventSlug,
            isInplay,
        };
    }

    const translation = await post(buildSpectateUrl(BREADCRUMBS_L10N_ENDPOINT), translationRequestData);

    const {
        sport, category, tournament, event, coupon,
    } = await translation.json();

    const translatedSportName = (sport && sport.label) ? sport.label : sportName;
    // Backup with originalUrl in case slug does not exist in sport data
    const translatedSportSlug = (sport && sport.slug) ? sport.slug : originalUrl[0];

    return {
        sportId,
        sportName: translatedSportName,
        sportSlug: translatedSportSlug,
        categoryId,
        categorySlug: category && category.slug ? category.slug : categorySlug,
        categoryName: category && category.label ? category.label : categoryName,
        tournamentId,
        tournamentSlug: tournament && tournament.slug ? tournament.slug : tournamentSlug,
        tournamentName: tournament && tournament.label ? tournament.label : tournamentName,
        eventId,
        eventSlug: event && event.slug ? event.slug : eventSlug,
        eventName: event && event.label ? event.label : eventName,
        couponId,
        couponSlug: coupon && coupon.slug ? coupon.slug : couponSlug,
        couponName: coupon && coupon.label ? coupon.label : couponName,

    };
}

/**
 * Builds the Seo Navigation Data for Homepage
 *
 * @returns {Object} The items for homepage
 */
const buildSeoNavHomepage = () => {
    const homepage = i18n.t('title_bar.titlebar');
    const titleBar = getTitleBar('homepage', {homepage});

    return {
        breadcrumbsList: null,
        titleBar,
    };
};

/**
 * Builds the breadcrumb item for a static page.
 *
 * @param {String} pageName The page name
 * @param {Object} originalUrlTokens The original (localized) url tokens
 * @param {Boolean|null} isInplay in case the static page is the inplay-home
 * @returns {Object} The item for a static page
 */
function buildSeoNavStaticPageItem(pageName, originalUrlTokens, isInplay) {
    const inplayUrlLength = 2;
    if (isInplay && originalUrlTokens.length > inplayUrlLength) {
        return buildSeoNavInplayItems(pageName, originalUrlTokens, {});
    }

    return {
        breadcrumbsList: [
            createLastVisibleBreadcrumbsItem(pageName, `/${originalUrlTokens.join('/')}`),
        ],
        titleBar: isInplay ?
            getTitleBar('inplaypage') :
            getTitleBar('staticpage', { pageName }),
    };
}

/**
 * Builds the breadcrumb item for inplay.
 *
 * @param {String} pageName The page name
 * @param {Object} originalUrlTokens The original (localized) url tokens
 * @param {Number} data The breadcrumbs data
 *
 * @returns {Object} with the one-item breadcrumbs for a static page
 */
function buildSeoNavInplayItems(pageName, originalUrlTokens, data) {
    const [ inplayRoot, matchMarkets ] = originalUrlTokens;
    const { sportId, sportName, sportSlug, eventId, eventName } = data;

    const isEventPage = eventId ? true : false;
    const isScheduledPage = !sportId && !isEventPage;

    const pageItem = createBreadcrumbsItem(pageName, seoUrlInplay());

    let breadcrumbsList = [];

    if (isScheduledPage) {
        const scheduleItem = createLastVisibleBreadcrumbsItem(i18n.t(TOP_MENU_SCHEDULE_TEXT), seoUrlInplayScheduled(`${inplayHomepageUrl()}/${inplayScheduledUrl()}`));

        breadcrumbsList = [ pageItem, scheduleItem ];
    } else {
        const sportItem = isEventPage ?
            createBreadcrumbsItem(sportName, seoUrlInplaySport(sportSlug, sportId)) :
            createLastVisibleBreadcrumbsItem(sportName, seoUrlInplaySport(sportSlug, sportId));

        const eventItem = isEventPage ?
            createLastVisibleBreadcrumbsItem(eventName, seoUrlInplayEvent(`/${inplayRoot}/${matchMarkets}/${eventId}`, eventId)) :
            createInvisibleBreadcrumbsItem(eventName, seoUrlInplayEvent(`/${inplayRoot}/${matchMarkets}/${eventId}`, eventId));

        breadcrumbsList = [ pageItem, sportItem, eventItem ];
    }

    let titleBar = null;

    if (sportId || isScheduledPage) {
        titleBar = getTitleBar('inplaypage');
    }

    if (isEventPage) {
        titleBar = getTitleBar('eventpage', { eventLabel: eventName });
    }

    return {
        breadcrumbsList,
        titleBar,
    };
}

/**
 * Builds the breadcrumb item for coupons.
 *
 * @param {String} pageName The page name
 * @param {Object} originalUrlTokens The original (localized) url tokens
 * @param {Number} data The breadcrumbs data
 *
 * @returns {Object} with breadcrumbsList for a static page
 */
function buildBreadcrumbsCouponsItems(pageName, originalUrlTokens, data) {
    const {
        sportId,
        sportName,
        sportSlug,
        couponId = null,
        couponSlug,
        couponName,
        tournamentId = null,
        tournamentSlug,
        tournamentName,
    } = data;
    const [, couponsSlug, categoryString] = originalUrlTokens;
    const isCouponLevel = (couponId !== null || tournamentId !== null);

    const sportItem = createBreadcrumbsItem(sportName, seoUrlSport(`/${sportSlug}`, sportId));

    const pageItem = isCouponLevel ?
        createBreadcrumbsItem(pageName, seoUrlCouponSport(`/${sportSlug}/${couponsSlug}`, sportId)) :
        createLastVisibleBreadcrumbsItem(pageName, seoUrlCouponSport(`/${sportSlug}/${couponsSlug}`, sportId));

    let breadcrumbsList = [ sportItem, pageItem ];

    if (couponId !== null) {
        breadcrumbsList.push(
            createLastVisibleBreadcrumbsItem(
                couponName,
                seoUrlCoupon(`/${sportSlug}/${couponsSlug}/${couponSlug}`, couponId)
            )
        );
    }

    if (tournamentId !== null) {
        breadcrumbsList.push(
            createLastVisibleBreadcrumbsItem(
                tournamentName,
                seoUrlCouponTournament(`/${sportSlug}/${couponsSlug}/${categoryString}/${tournamentSlug}`, tournamentId)
            )
        );
    }

    return {
        breadcrumbsList,
        titleBar: sportName ?
            getTitleBar('couponspage', { sportLabel: sportName }) : null,
    };
}

/**
 * Build the navigation data for racing
 * @param {Object} data Breadcrumbs data
 * @returns {Object} Breadcrumbs list and title-bar
 */
function buildSeoNavRacing(data) {
    const {
        sportId, sportName, sportSlug,
        categorySlug,
        tournamentSlug,
        eventId = null, eventName, eventSlug, scheduledStart = null,
        eventExtraInfo = {},
        context,
    } = data;
    const { sportPageName, sportPageUrl } = getSportsPageBreadcrumb(context, sportSlug, sportId);
    const { is_antepost: isAntepost } = eventExtraInfo;

    const sportItem = createBreadcrumbsItem(sportName, seoUrlSport(sportSlug, sportId));

    let breadcrumbsList = [ sportItem ];
    let titleBar = getTitleBar('sportpage', { sportLabel: sportItem.label });

    if (sportPageName) {
        const pageItem = createBreadcrumbsItem(sportPageName, sportPageUrl);
        breadcrumbsList.push(pageItem);
    }

    if (eventId !== null) {
        if (!isAntepost) {
            const backToMeetingsUrl = seoUrlRacing(`${sportSlug}/${i18n.t('url.meetings')}`, sportId);

            breadcrumbsList.push(
                createBreadcrumbsItem(i18n.t('racing.meetings'), backToMeetingsUrl)
            );
        }
        if (isAntepost){
            const backToAntePostUrl = seoUrlSportAntepost(`${sportSlug}/${i18n.t('url.ante-post')}`, sportId);

            breadcrumbsList.push(
                createBreadcrumbsItem(i18n.t('racing.ante-post'), backToAntePostUrl)
            );
        }
        const eventUrl = `${sportSlug}/${categorySlug}/${tournamentSlug}/${eventSlug}`;
        let eventNameTime = eventName;

        if (scheduledStart) {
            const time = format(new Date(scheduledStart), EVENT_TIME_FORMAT);
            eventNameTime = `${eventName} ${time}`;
        }

        breadcrumbsList.push(
            createBreadcrumbsItem(eventNameTime, seoUrlEvent(eventUrl, eventId))
        );
        titleBar = getTitleBar('eventpage', { eventLabel: eventName });
    }

    const lastItem = breadcrumbsList?.length - 1;
    breadcrumbsList[lastItem] = { ...breadcrumbsList[lastItem], lastVisible: true };

    return {
        breadcrumbsList,
        titleBar,
    };
}

/**
 * Build the navigation data for virtual sports
 * @param {Object} data Breadcrumbs data
 * @returns {Object} Breadcrumbs list and title-bar
 */
function buildSeoNavVirtuals(data) {
    const {
        sportId, sportName, sportSlug,
        eventId = null, eventName, eventSlug,
    } = data;
    const sportItem = createBreadcrumbsItem(sportName, seoUrlSport(sportSlug, sportId), eventId === null);
    let breadcrumbsList = [ sportItem ];
    let titleBar = getTitleBar('sportpage', { sportLabel: sportItem.label });

    if (eventId !== null) {
        const eventUrl = `${sportSlug}/${eventSlug}`;

        breadcrumbsList.push(
            createLastVisibleBreadcrumbsItem(eventName, seoUrlEvent(eventUrl, eventId))
        );
        titleBar = getTitleBar('eventpage', { eventLabel: eventName });
    }

    return {
        breadcrumbsList,
        titleBar,
    };
 }

 /**
 * Build the navigation data for MotorSport
 * @param {Object} data Breadcrumbs data
 * @returns {Object} Breadcrumbs list and title-bar
 */
function buildSeoNavMotorSport(data) {

    const {
        sportId, sportName, sportSlug,
        eventId = null, eventName, eventSlug,
        tournamentId = null, tournamentSlug, tournamentName,
        categorySlug,
    } = data;

    const sportItem = createBreadcrumbsItem(sportName, seoUrlSport(sportSlug, sportId), eventId === null);
    let breadcrumbsList = [ sportItem ];
    let titleBar = getTitleBar('sportpage', { sportLabel: sportItem.label });

    if (eventId !== null) {
        const eventUrl = `${sportSlug}/${eventSlug}`;

        breadcrumbsList.push(
            createLastVisibleBreadcrumbsItem(eventName, seoUrlEvent(eventUrl, eventId))
        );
        titleBar = getTitleBar('eventpage', { eventLabel: eventName });
    } else if (tournamentId !== null) {
        const outrightSlug = i18n.t('url.outrights');
        const isOutright = data.systemUrlPath.endsWith('outrights');
        const tournamentUrl = `${sportSlug}/${categorySlug}/${tournamentSlug}${isOutright ? `/${outrightSlug}` : ''}`;
        const tournamentItem = createLastVisibleBreadcrumbsItem(tournamentName, seoUrlTournamentOutrights(tournamentUrl, tournamentId));

        breadcrumbsList.push(tournamentItem);
        titleBar = getTitleBar('competitionpage', { competitionLabel: tournamentItem.label });

    }

    return {
        breadcrumbsList,
        titleBar,
    };
 }

/**
 *
 * @param {String} template subprop in the titleBar portion of JSON translations file
 * @param {String} params map of template parameters
 * @return {Object} Seo Nav TitleBar structure
 */
export function getTitleBar(template, params) {
    return {
        translationTemplateKey: `title_bar.${template}`,
        params,
    };
}

/**
 * Build the navigation data for dynamic items (ie: sport pages)
 * @param {Object} data Breadcrumbs data
 * @returns {Object} Breadcrumbs list and title-bar
 */
function buildSeoNavDynamic(data) {
    const {
        sportId,
        sportName,
        sportSlug,
        categorySlug,
        tournamentId = null,
        tournamentName,
        tournamentSlug,
        eventId = null,
        eventName,
        eventSlug,
        context = null,
        contextId,
    } = data;
    const sportItem = createBreadcrumbsItem(sportName, seoUrlSport(sportSlug, sportId), tournamentId === null);
    let breadcrumbsList = [ sportItem ];
    let titleBar = getTitleBar('sportpage', { sportLabel: sportItem.label });

    const isEventLevel = (eventId !== null);

    if (tournamentId !== null) {
        const outrightsSlug = i18n.t('url.outrights');
        const isOutright = data.systemUrlPath.endsWith('outrights'); // systemUrl is generated untranslated as '..../outrights'
        const tournamentUrl = `${sportSlug}/${categorySlug}/${tournamentSlug}${isOutright ? `/${outrightsSlug}` : ''}`;

        const sportPageName = i18n.t('sports.competitions');
        const sportPageUrl = seoUrlCompetitionSport(`${sportSlug}/${i18n.t('url.competitions')}/`, sportId);

        breadcrumbsList.push(createBreadcrumbsItem(sportPageName, sportPageUrl));

        const seoUrlTournamentFunc = !isOutright ? seoUrlTournament : seoUrlTournamentOutrights;
        const tournamentItem = !isEventLevel ?
        createLastVisibleBreadcrumbsItem(tournamentName, seoUrlTournamentFunc(tournamentUrl, tournamentId))
        : createBreadcrumbsItem(tournamentName, seoUrlTournamentFunc(tournamentUrl, tournamentId));

        breadcrumbsList.push(tournamentItem);
        titleBar = getTitleBar('competitionpage', { competitionLabel: tournamentItem.label });
    }

    if (isEventLevel && context !== SEO_URL_SUFFIX_TOURNAMENT_OUTRIGHT) {
        const eventUrl = `${sportSlug}/${categorySlug}/${tournamentSlug}/${eventSlug}`;

        breadcrumbsList.push(
            createLastVisibleBreadcrumbsItem(eventName, seoUrlEvent(eventUrl, eventId))
        );
        titleBar = getTitleBar('eventpage', { eventLabel: eventName });
    }

    if (context === SEO_URL_SUFFIX_TOURNAMENT_OUTRIGHT) {
        const eventUrl = seoUrlTournamentOutright(`${sportSlug}/${categorySlug}/${tournamentSlug}/${i18n.t('url.outrights')}/`, contextId);
        const backToOutrightUrl = seoUrlTournamentOutrights(`${sportSlug}/${categorySlug}/${tournamentSlug}/${i18n.t('url.outrights')}/`, tournamentId);

        breadcrumbsList.push(
            createBreadcrumbsItem(i18n.t('sports.outrights'), backToOutrightUrl),
            createLastVisibleBreadcrumbsItem(eventName, eventUrl)
        );
    }

    return {
        breadcrumbsList,
        titleBar,
    };
}

const buildBreadcrumbsForSportPage = ({
    sportName,
    sportSlug,
    sportId,
    context,
}, engSportName) => {
    const { sportPageName, sportPageUrl } = getSportsPageBreadcrumb(context, sportSlug, sportId);
    const sportItem = !sportPageName ?
    createLastVisibleBreadcrumbsItem(sportName, seoUrlSport(sportSlug, sportId)) :
    createBreadcrumbsItem(sportName, seoUrlSport(sportSlug, sportId));

    let breadcrumbsList = [ sportItem ];

    let titleBar = getTitleBar(engSportName === 'Odds Boost' ? 'oddsBoost' : 'sportpage', { sportLabel: sportItem.label });

    if (sportPageName) {
        const pageItem = createLastVisibleBreadcrumbsItem(sportPageName, sportPageUrl);
        breadcrumbsList = [ sportItem, pageItem ];
    }

    return {
        breadcrumbsList,
        titleBar,
    };
};
